/**
 * Shared arg parser for the report content-edit commands — `wayai report edit`
 * (reporter) and `wayai admin report update` (admin). Both amend the same five
 * content fields of an EditReportBody, so the flag→key mapping and the
 * missing-value handling live in one place.
 *
 * Critically, this distinguishes a flag given WITHOUT a value (an error) from a
 * flag given an explicit empty string (a deliberate clear, e.g. `--error ""`).
 * The generic `parseReportFlags` in admin.ts cannot — it collapses a value-less
 * flag to the string `"true"`, which would silently persist `"true"` into a
 * content field. Hence a dedicated parser for the value-bearing content flags.
 */

import type { EditReportBody } from '@wayai/core/contracts';

type ContentKey = keyof EditReportBody;

// Long + short flag aliases → EditReportBody key. `error`/`steps`/`context` map
// to the raw_payload field names (error_message/steps_to_reproduce/additional_context).
const FLAG_TO_KEY: Record<string, ContentKey> = {
  '-t': 'title',
  '--title': 'title',
  '-d': 'description',
  '--description': 'description',
  '-e': 'error_message',
  '--error': 'error_message',
  '-s': 'steps_to_reproduce',
  '--steps': 'steps_to_reproduce',
  '--context': 'additional_context',
};

/** Every content flag the parser reads, each taking a value (`""` included). */
export const REPORT_EDIT_FLAGS: readonly string[] = Object.keys(FLAG_TO_KEY);

export interface ParsedReportEdit {
  /** First non-flag positional argument (the report id), if present. */
  reportId?: string;
  patch: EditReportBody;
  /** Set when parsing failed (unknown flag / missing value). Callers print + exit. */
  error?: string;
}

/**
 * Parse `[<id>] [--flag value ...]` into a report id + content patch. A content
 * flag with no following token errors (`--<flag> requires a value`); an explicit
 * empty string is kept (it clears the field server-side). An unknown `-`/`--`
 * flag errors rather than being silently dropped.
 */
export function parseReportEditArgs(args: string[]): ParsedReportEdit {
  const patch: EditReportBody = {};
  let reportId: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    const key = FLAG_TO_KEY[arg];
    if (key) {
      const value = args[i + 1];
      if (value === undefined) {
        return { reportId, patch, error: `${arg} requires a value` };
      }
      patch[key] = value;
      i++; // consume the value (may be "" → clears the field)
    } else if (arg.startsWith('-')) {
      return { reportId, patch, error: `Unknown flag: ${arg}` };
    } else if (reportId === undefined) {
      reportId = arg;
    }
    // Extra positionals beyond the id are ignored.
  }

  return { reportId, patch };
}
